import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useClientPlants from "../../../../../hooks/useClientPlants";
import { projectTypeStoredName } from "../../../../../constants/projectTypes";
import {
  PROJECT_SAVED_EVENT,
  notifyPlantUpdated,
  notifyWbsUpdated,
} from "../utils/projectSavedEvent";
import {
  createPlantRecord,
  deletePlantRecord,
  fetchPlantMcsTree,
  fetchPlantWbs,
  updatePlantRecord,
} from "../api/plantsApi";
import {
  createWbsRecord,
  deleteWbsRecord,
  renameWbsRecord,
  seedFirstRevision,
} from "../api/wbsApi";
import {
  createNumberedWbsDocument,
  fetchPvInputData,
  fetchWbsDocuments,
  fetchWbsDrawings,
} from "../api/registersApi";
import { isPvWbs, wbsLabel } from "../utils/treeLabels";
import { extractPvRevisionEntries } from "../utils/revisionEntries";
import { normalizeMcsTreeData } from "../utils/mcsTreeData";
import {
  SEARCH_DEBOUNCE_MS,
  buildAutoExpandIds,
  isDocVisible as matchDocVisible,
  isPlantVisible as matchPlantVisible,
  isWbsVisible as matchWbsVisible,
} from "../utils/treeSearch";

// Kept here (rather than only in utils/) so every existing import of these
// helpers - inside the tree and in PlantModuleNav, SummaryPanel,
// DocumentFilesPanel - keeps working unchanged.
export {
  documentAbbreviations,
  docLabel,
  docNumberLabel,
  drawingLabel,
  drawingsOf,
  hasDrawingMatch,
  hasDrawings,
  isPipOrStrucWbs,
  isPvWbs,
  matches,
  matchesDrawing,
  wbsLabel,
} from "../utils/treeLabels";
export {
  WBS_GROUPS,
  isSoftwareWbs,
  treeGroupKey,
  wbsGroupKey,
  wbsGroupNodeId,
} from "../utils/wbsGroups";
export {
  extractProjectEstimationRevisions,
  extractPvRevisionEntries,
} from "../utils/revisionEntries";
export {
  extractPlantBidEvaluations,
  extractPlantPos,
  normalizeMcsTreeData,
} from "../utils/mcsTreeData";

/**
 * Data/state layer behind the Plant -> WBS -> Documents/Input Data/
 * Estimation/2D Drawings tree, shared by the global AppSidebar's
 * ProjectsTreeSection and the full-page EmsExplorer. Kept separate from
 * rendering so both consumers load and search the exact same way; each
 * renders it (and reacts to node clicks) differently.
 *
 * HTTP lives in `../api/`, pure derivations in `../utils/` - this hook only
 * holds the cached state and wires the two together.
 */
export default function useEmsProjectsTreeData() {
  const { plants, loading: plantsLoading, reloadPlants, patchPlant } =
    useClientPlants();
  const clientName = process.env.REACT_APP_CLIENT_NAME;

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef(null);

  // plantId -> "loading" | array of WBS
  const [wbsByPlant, setWbsByPlant] = useState({});
  // wbsId -> "loading" | array of documents
  const [docsByWbs, setDocsByWbs] = useState({});
  // wbsId -> "loading" | array of { revision, documentUID, estimationDocumentUID }
  const [pvInputsByWbs, setPvInputsByWbs] = useState({});
  // wbsId -> "loading" | array of drawing records (MCS3 Drawing Register)
  const [drawingsByWbs, setDrawingsByWbs] = useState({});
  // plantId -> "loading" | MCS tree data { mrqs, mrvs, srvs }
  const [mcsTreeByPlant, setMcsTreeByPlant] = useState({});
  const [expanded, setExpanded] = useState([]);

  // `silent` refreshes without dropping the list into its loading state -
  // for a refetch that only confirms a change already applied locally, where
  // a "Loading WBS…" flash would collapse the subtree the user is looking at.
  const loadWbsForPlant = useCallback(
    (plantId, { force = false, silent = false } = {}) => {
      setWbsByPlant((prev) => {
        if (prev[plantId] && !force) return prev;
        if (prev[plantId] && silent) return prev;
        return { ...prev, [plantId]: "loading" };
      });
      fetchPlantWbs(plantId, clientName)
        .then((plantProjects) => {
          setWbsByPlant((prev) => ({ ...prev, [plantId]: plantProjects }));
        })
        .catch(() => {
          setWbsByPlant((prev) => ({ ...prev, [plantId]: [] }));
        });
    },
    [clientName],
  );

  const loadDocsForWbs = useCallback((wbsId, { force = false } = {}) => {
    setDocsByWbs((prev) => {
      if (prev[wbsId] && !force) return prev;
      return { ...prev, [wbsId]: "loading" };
    });
    fetchWbsDocuments(wbsId)
      .then((docs) => {
        setDocsByWbs((prev) => ({ ...prev, [wbsId]: docs }));
      })
      .catch(() => {
        setDocsByWbs((prev) => ({ ...prev, [wbsId]: [] }));
      });
  }, []);

  const createPlant = useCallback(
    async ({
      clientId,
      namePrefix,
      nameNumber,
      description,
      sharepointLink,
    }) => {
      await createPlantRecord({
        name: [namePrefix, nameNumber].filter(Boolean).join("-"),
        clientId: clientId || null,
        description: description?.trim() || null,
        sharepointLink: sharepointLink?.trim() || undefined,
      });
      reloadPlants();
    },
    [reloadPlants],
  );

  const updatePlant = useCallback(
    async (plantId, { name, description }) => {
      const changes = { name, description: description?.trim() || "" };

      await updatePlantRecord(plantId, changes);

      // In the tree at once, then confirmed by the refetch; and out to the
      // module headers, which show this name as PROJECT# UID and build the
      // first half of every Rev UID from it.
      patchPlant(plantId, changes);
      notifyPlantUpdated(plantId, changes);
      reloadPlants();
    },
    [patchPlant, reloadPlants],
  );

  // Deletes a project and every WBS under it (the backend removes those
  // first - see deletePlant in plant.service.ts - since a WBS's revisions
  // and saved data cascade from the WBS, not from here). Rows in the
  // plant's other registers (MRQs, change orders, tasks) still block the
  // delete; the caller shows what the backend said.
  const deletePlant = useCallback(
    async (plantId) => {
      await deletePlantRecord(plantId);

      // Everything cached under it goes too, so a later plant can't show
      // the deleted one's WBS while its own list loads.
      setWbsByPlant((prev) => {
        const { [plantId]: removed, ...rest } = prev;
        return rest;
      });

      reloadPlants();
    },
    [reloadPlants],
  );

  // Creates a new WBS/project under a plant (same endpoint as the
  // Project/plant page's SubProjectsCrudModal), gives it its Rev 0, then
  // force-refreshes that plant's WBS list so the tree reflects it immediately.
  const createWbs = useCallback(
    async (plantId, { projectName, description, type, link }) => {
      const created = await createWbsRecord({
        projectName,
        clientName,
        link: link || "",
        // Shown under the WBS UID in the module headers (see
        // LoadProjectDataComboBox), the same way a plant's description sits
        // under Project# UID.
        description: description?.trim() || undefined,
        // Saved under the full type name ("Estimation - Piping"), not the code.
        type: projectTypeStoredName(type),
        plantId,
      });
      // The plant's name is half of the revision's document UID
      // ({PLANT}-{WBS}-R0), and only the tree has it here - the create
      // response carries the WBS alone.
      const plantName = (plants || []).find((p) => p?.id === plantId)?.name;
      await seedFirstRevision(created?.id, type, {
        plantName,
        wbsName: created?.name || projectName,
      });
      loadWbsForPlant(plantId, { force: true });
    },
    [clientName, loadWbsForPlant, plants],
  );

  const updateWbs = useCallback(
    async (plantId, wbsId, { projectName, description }) => {
      const changes = {
        name: projectName,
        // "" is meaningful - it clears the description.
        description: description ?? "",
      };

      await renameWbsRecord(wbsId, changes);

      // Straight into the row the user is looking at...
      setWbsByPlant((prev) => {
        const list = prev[plantId];
        if (!Array.isArray(list)) return prev;

        return {
          ...prev,
          [plantId]: list.map((wbs) =>
            String(wbs?.id) === String(wbsId) ? { ...wbs, ...changes } : wbs,
          ),
        };
      });

      // ...out to the module pages and WBS pickers holding their own copy
      // of this record...
      notifyWbsUpdated(wbsId, changes);

      // ...and a quiet refetch behind both, to confirm what was stored.
      loadWbsForPlant(plantId, { force: true, silent: true });
    },
    [loadWbsForPlant],
  );

  // Deletes a WBS and everything the backend cascades with it (its
  // revisions, its PV component data). Rows that do NOT cascade - documents
  // in the register, drawings, RACI - make the backend refuse the delete;
  // the caller shows that error rather than the tree pretending it worked.
  const deleteWbs = useCallback(
    async (plantId, wbsId) => {
      await deleteWbsRecord(wbsId);

      // Drop what was cached against it, so a new WBS that reuses the id
      // can't inherit the deleted one's documents or revisions.
      setDocsByWbs((prev) => {
        const { [wbsId]: removed, ...rest } = prev;
        return rest;
      });
      setPvInputsByWbs((prev) => {
        const { [wbsId]: removed, ...rest } = prev;
        return rest;
      });
      setDrawingsByWbs((prev) => {
        const { [wbsId]: removed, ...rest } = prev;
        return rest;
      });

      loadWbsForPlant(plantId, { force: true });
    },
    [loadWbsForPlant],
  );

  const createDocument = useCallback(
    async (
      plant,
      wbs,
      { abbreviation, type, revision, description, numberOfSheets },
    ) => {
      const existingDocs = Array.isArray(docsByWbs[wbs.id])
        ? docsByWbs[wbs.id]
        : [];

      await createNumberedWbsDocument({
        wbsId: wbs.id,
        plantName: plant?.name,
        wbsName: wbsLabel(wbs),
        existingDocs,
        abbreviation,
        type,
        revision,
        description,
        numberOfSheets,
      });
      loadDocsForWbs(wbs.id, { force: true });
    },
    [docsByWbs, loadDocsForWbs],
  );

  // Plant + WBS names, kept in a ref so loadPvInputsForWbs can stay
  // dependency-free (callers list it in effect deps; re-creating it on every
  // WBS-list change would re-fire their fetches).
  const pvNameSourcesRef = useRef({ plants: [], wbsByPlant: {} });
  pvNameSourcesRef.current = { plants, wbsByPlant };

  // The names behind a PV revision's document UID: the WBS's own name and
  // the name of the plant whose list it came from.
  const resolvePvNames = useCallback((wbsId) => {
    const { plants: knownPlants, wbsByPlant: lists } = pvNameSourcesRef.current;
    for (const [plantId, list] of Object.entries(lists || {})) {
      if (!Array.isArray(list)) continue;
      const wbs = list.find((item) => String(item?.id) === String(wbsId));
      if (!wbs) continue;
      return {
        plantName: (knownPlants || []).find((plant) => plant?.id === plantId)
          ?.name,
        wbsName: wbsLabel(wbs),
      };
    }
    return {};
  }, []);

  const loadPvInputsForWbs = useCallback(
    (wbsId) => {
      setPvInputsByWbs((prev) => {
        if (prev[wbsId]) return prev;
        return { ...prev, [wbsId]: "loading" };
      });
      fetchPvInputData(wbsId)
        .then((res) => {
          setPvInputsByWbs((prev) => ({
            ...prev,
            [wbsId]: extractPvRevisionEntries(res, resolvePvNames(wbsId)),
          }));
        })
        .catch(() => {
          setPvInputsByWbs((prev) => ({ ...prev, [wbsId]: [] }));
        });
    },
    [resolvePvNames],
  );

  const loadDrawingsForWbs = useCallback((wbsId) => {
    setDrawingsByWbs((prev) => {
      if (prev[wbsId]) return prev;
      return { ...prev, [wbsId]: "loading" };
    });
    fetchWbsDrawings(wbsId)
      .then((drawings) => {
        setDrawingsByWbs((prev) => ({ ...prev, [wbsId]: drawings }));
      })
      .catch(() => {
        setDrawingsByWbs((prev) => ({ ...prev, [wbsId]: [] }));
      });
  }, []);

  const loadMcsTreeForPlant = useCallback((plantId) => {
    setMcsTreeByPlant((prev) => {
      if (prev[plantId]) return prev;
      return { ...prev, [plantId]: "loading" };
    });
    fetchPlantMcsTree(plantId)
      .then((raw) => {
        // POs/Bids arrive embedded on each MRQ (`mrq.pos` / `mrq.bids`), so
        // flatten them here into plant-level `pos` / `bids` for the tree.
        setMcsTreeByPlant((prev) => ({
          ...prev,
          [plantId]: normalizeMcsTreeData(raw),
        }));
      })
      .catch(() => {
        setMcsTreeByPlant((prev) => ({ ...prev, [plantId]: {} }));
      });
  }, []);

  const handleSearchChange = (value) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value.trim());
    }, SEARCH_DEBOUNCE_MS);
  };

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Structure/Piping keep their Estimation revisions inside the WBS list
  // response, which is cached per plant - so after a module saves, reload the
  // plant(s) holding that WBS, otherwise new revisions only show on a refresh.
  const wbsByPlantRef = useRef(wbsByPlant);
  wbsByPlantRef.current = wbsByPlant;
  useEffect(() => {
    const handleProjectSaved = (event) => {
      const projectId = String(event.detail?.projectId ?? "");
      if (!projectId) return;
      Object.entries(wbsByPlantRef.current).forEach(([plantId, list]) => {
        if (
          Array.isArray(list) &&
          list.some((wbs) => String(wbs?.id) === projectId)
        ) {
          loadWbsForPlant(plantId, { force: true });
        }
      });
    };
    window.addEventListener(PROJECT_SAVED_EVENT, handleProjectSaved);
    return () =>
      window.removeEventListener(PROJECT_SAVED_EVENT, handleProjectSaved);
  }, [loadWbsForPlant]);

  const query = debouncedSearch.toLowerCase();

  // Searching needs every WBS (and every WBS's documents) loaded to match
  // against — eager-load everything once a query is active.
  useEffect(() => {
    if (!query) return;
    plants.forEach((plant) => {
      if (!wbsByPlant[plant.id]) loadWbsForPlant(plant.id);
    });
  }, [query, plants, wbsByPlant, loadWbsForPlant]);

  useEffect(() => {
    if (!query) return;
    Object.values(wbsByPlant).forEach((list) => {
      if (!Array.isArray(list)) return;
      list.forEach((wbs) => {
        if (!docsByWbs[wbs.id]) loadDocsForWbs(wbs.id);
      });
    });
  }, [query, wbsByPlant, docsByWbs, loadDocsForWbs]);

  const isSearchLoading =
    Boolean(query) &&
    (Object.values(wbsByPlant).some((v) => v === "loading") ||
      Object.values(docsByWbs).some((v) => v === "loading"));

  // What a WBS was left open from before the search is collapsed first, by the
  // effect below - otherwise a WBS the user had drilled into reappears among
  // the results with its documents, revisions and drawings unfurled, looking
  // like the search expanded it.
  const autoExpandIds = useMemo(
    () =>
      buildAutoExpandIds({
        plants,
        query,
        wbsByPlant,
        docsByWbs,
        drawingsByWbs,
      }),
    [query, plants, wbsByPlant, docsByWbs, drawingsByWbs],
  );

  // Keyed off the query itself rather than autoExpandIds, which keeps changing
  // while a search's documents load - re-running this on that would fight the
  // user expanding a WBS to look inside a result.
  const lastQueryRef = useRef(query);
  useEffect(() => {
    if (query === lastQueryRef.current) return;
    lastQueryRef.current = query;
    if (!query) return;
    // Plants and discipline folders are what autoExpandIds is about to open
    // anyway; everything deeper belonged to the view the search replaced.
    setExpanded((prev) =>
      prev.filter((id) => id.startsWith("plant-") || id.startsWith("grp-")),
    );
  }, [query]);

  useEffect(() => {
    if (!autoExpandIds) return;
    setExpanded((prev) => Array.from(new Set([...prev, ...autoExpandIds])));
  }, [autoExpandIds]);

  const findWbsById = useCallback(
    (wbsId) => {
      for (const list of Object.values(wbsByPlant)) {
        if (!Array.isArray(list)) continue;
        const found = list.find((wbs) => wbs.id === wbsId);
        if (found) return found;
      }
      return null;
    },
    [wbsByPlant],
  );

  const handleNodeToggle = (event, nodeIds) => {
    setExpanded(nodeIds);
    const newlyExpanded = nodeIds.filter((id) => !expanded.includes(id));
    newlyExpanded.forEach((id) => {
      // The Procurement / Project Control headings are built by appending
      // "-<name>-section" onto their plant's id, so they still start with
      // "plant-" - excluded for the same reason the category folders below
      // are, since fetching against "<plantId>-procurement-section" asks the
      // backend for a plant that does not exist.
      if (id.startsWith("plant-") && !id.endsWith("-section")) {
        const plantId = id.slice("plant-".length);
        loadWbsForPlant(plantId);
        loadMcsTreeForPlant(plantId);
      } else if (id.startsWith("wbs-") && !id.includes("-cat-")) {
        // Category folder ids are built by appending "-cat-<name>" onto their
        // parent wbs id, so they still start with "wbs-" - excluded here,
        // otherwise expanding one re-fires the fetches below with a bogus
        // "id-cat-..." wbsId instead of the real one.
        //
        // Drawings ride on the WBS record itself (wbs.drawings), so expanding
        // a WBS needs no drawing fetch. The EMS Explorer's summary panel
        // loads its own copy via loadDrawingsForWbs.
        const wbsId = id.slice("wbs-".length);
        loadDocsForWbs(wbsId);
        if (isPvWbs(findWbsById(wbsId))) {
          loadPvInputsForWbs(wbsId);
        }
      }
    });
  };

  const isPlantVisible = (plant) =>
    matchPlantVisible(plant, query, wbsByPlant, docsByWbs, drawingsByWbs);

  const isWbsVisible = (plant, wbs) =>
    matchWbsVisible(plant, wbs, query, docsByWbs, drawingsByWbs);

  const isDocVisible = (plant, wbs, doc) =>
    matchDocVisible(plant, wbs, doc, query);

  return {
    plants,
    plantsLoading,
    search,
    handleSearchChange,
    isSearchLoading,
    query,
    wbsByPlant,
    docsByWbs,
    pvInputsByWbs,
    drawingsByWbs,
    mcsTreeByPlant,
    loadWbsForPlant,
    loadDocsForWbs,
    loadPvInputsForWbs,
    loadDrawingsForWbs,
    loadMcsTreeForPlant,
    createPlant,
    updatePlant,
    deletePlant,
    createWbs,
    updateWbs,
    deleteWbs,
    createDocument,
    expanded,
    setExpanded,
    handleNodeToggle,
    isPlantVisible,
    isWbsVisible,
    isDocVisible,
  };
}
